import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Trash2, X } from "lucide-react";
import { Btn, Section } from "@/components/account/Shell";
import { Cell, Dash, Filters, IconBtn, PillRow, Sheet, Tag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";
import { themeOf } from "@/lib/mall";

const FILTERS = [["pending", "In review"], ["active", "Approved"], ["rejected", "Sent back"], ["", "All"]];
const COLS = [
    { key: "shop", label: "Shop", sub: "who wrote it", w: "minmax(170px,1.1fr)" },
    { key: "code", label: "Code", sub: "what shoppers type", w: "130px" },
    { key: "title", label: "Title", sub: "the line on the ticket", w: "minmax(200px,1.5fr)" },
    { key: "value", label: "Value", w: "90px" },
    { key: "min", label: "Minimum", sub: "$ bag before it works", w: "100px" },
    { key: "expires", label: "Through", w: "110px" },
    { key: "used", label: "Used", sub: "times redeemed", w: "70px" },
    { key: "status", label: "Status", sub: "in review · approved · sent back · switched off", w: "150px" },
    { key: "actions", label: "Actions", sub: "approve · send back · remove", w: "210px" },
];

export default function CouponsAdmin() {
    const [status, setStatus] = useState("pending");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        api.get("/platform/coupons", { params: status ? { status } : {} })
            .then(({ data }) => setData(data)).catch(() => setData({ coupons: [], counts: {} }));
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const review = async (id, decision) => {
        const note = decision === "reject" ? window.prompt("What should the shop fix?") ?? "" : "";
        setBusy(id);
        try {
            await api.post(`/platform/coupons/${id}/review`, { decision, note });
            toast.success(decision === "approve" ? "Coupon hangs in the book" : decision === "reject" ? "Sent back to the shop" : "Coupon removed");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-coupons">
            <Filters testid="admin-coupons-filters">
                <PillRow label="Show" testid="coupons-filter" value={status} onPick={setStatus}
                    choices={FILTERS.map(([key, label]) => ({ key, label, count: data?.counts?.[key] }))} />
            </Filters>
            <div className="mt-3">
                {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4a4a4a]" /></div> : (
                    <Section title={`${data.coupons.length} coupons`} pad={false} testid="admin-coupons-sheet" note="shops send coupons here from their back office">
                        <Sheet cols={COLS} rows={data.coupons} rowKey={(c) => c.id} testid="coupons-sheet" minWidth={1300}
                            rowTestid={(c) => `admin-coupon-${c.code}`} empty="Nothing here. Shops send coupons to 13thav support from their back end."
                            cells={(c) => {
                                const theme = themeOf(c.store);
                                return {
                                    shop: (
                                        <Cell>
                                            <span className="h-3 w-3 shrink-0 border border-black/10" style={{ background: theme.primary }} aria-hidden />
                                            <Two a={c.store?.name || c.store_name} b={c.store?.tier_label} />
                                        </Cell>
                                    ),
                                    code: <Cell><Txt bold mono>{c.code}</Txt></Cell>,
                                    title: <Cell><Two a={c.title} b={c.terms} /></Cell>,
                                    value: <Cell><Txt bold>{c.value_label}</Txt></Cell>,
                                    min: <Cell>{c.min_subtotal > 0 ? <Txt mono right>${c.min_subtotal}</Txt> : <Dash why="No minimum" />}</Cell>,
                                    expires: <Cell>{c.expires_at ? <Txt>{c.expires_at}</Txt> : <Dash why="No expiry" />}</Cell>,
                                    used: <Cell><Txt mono right>{c.redemptions || 0}</Txt></Cell>,
                                    status: (
                                        <Cell className="flex-wrap">
                                            <Tag status={c.status} testid={`coupon-status-${c.code}`} />
                                            {!c.active && <Tag status="removed">off</Tag>}
                                        </Cell>
                                    ),
                                    actions: (
                                        <Cell className="justify-end">
                                            {c.status !== "active" && (
                                                <Btn tone="solid" onClick={() => review(c.id, "approve")} disabled={busy === c.id} testid={`coupon-approve-${c.code}`}><Check size={10} /> Approve</Btn>
                                            )}
                                            {c.status !== "rejected" && (
                                                <Btn onClick={() => review(c.id, "reject")} disabled={busy === c.id} testid={`coupon-reject-${c.code}`}><X size={10} /> Send back</Btn>
                                            )}
                                            <IconBtn onClick={() => review(c.id, "remove")} disabled={busy === c.id} testid={`coupon-remove-${c.code}`} title="Remove this coupon" danger><Trash2 size={12} /></IconBtn>
                                        </Cell>
                                    ),
                                };
                            }} />
                    </Section>
                )}
            </div>
        </div>
    );
}
